import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar, type IconName } from '@beyond-js/ui/react';
import { useFamily } from '@cdn/family';
import { useI18n } from '@cdn/i18n';
import { useSession } from '@cdn/identity';
import type { Paths } from '@cdn/navigation';

/**
 * The narrowest viewport, in CSS pixels, with a permanent sidebar. Below it the sections are the product row
 * and its drawer. Measured on the administration's widest screens with long names in English and Spanish:
 * at this width and above, beside the 250 px sidebar, no screen scrolls sideways and no table or tab row scrolls
 * inside its region, except a release's outputs table, which is wider than most windows by design (the
 * measurement and its screens are in `docs/shared-ui.md`).
 */
export const CUT = 1024;

/**
 * CDN's own sections, drawn by the family's `Sidebar` (`@beyond-js/ui` 0.5.0, its copy since 0.7.2): a permanent sidebar from `CUT` on,
 * and below it a product row that names the section in view and opens the same links in a modal drawer. The
 * organization is not chosen here: the family bar's organization menu does that (D03). The plan of the
 * organization is the plan entry's note, and Projects appears only when this installation uses Beyond
 * Projects. A followed link is routed without a reload; the route's own focus handling then moves focus to the
 * new page's title. On a phone it merges with the family bar (`merge`, ui 0.16.0, DQ-19): one bar whose menu
 * button opens the drawer. ⌘K belongs to the page's `Palette`, so the sidebar keeps no shortcut of its own.
 */
export function Sections({ paths }: { paths: Paths }) {
	const { t, term, locale } = useI18n();
	const session = useSession();
	const family = useFamily();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	// The family's words, one frozen object per language: a new `labels` object creates a new sidebar
	const labels = Sidebar.labels[locale.code === 'es' ? 'es' : 'en'];
	const plan = session.organization?.plan.plan;
	const current = (path: string) => pathname === path || pathname.startsWith(`${path}/`);

	const items = [
		{ label: t('shell.nav.applications'), glyph: 'grid' as IconName, href: paths.applications },
		!family.standalone && { label: t('shell.nav.projects'), glyph: 'folder' as IconName, href: paths.projects },
		{ label: t('shell.nav.consumption'), glyph: 'graph' as IconName, href: paths.consumption },
		{ label: t('shell.nav.plan'), glyph: 'coins' as IconName, href: paths.plan, meta: plan ? term(`plan.${plan}`) : null },
		session.can('providers.manage') && { label: t('shell.nav.providers'), glyph: 'plug' as IconName, href: paths.providers }
	]
		.filter((item): item is { label: string; glyph: IconName; href: string; meta?: string | null } => Boolean(item))
		.map(item => ({ ...item, current: current(item.href) }));

	return (
		<Sidebar
			product="CDN"
			navigation={{ key: 'cdn' }}
			cut={CUT}
			merge
			groups={[{ items }]}
			labels={labels}
			onNavigate={item => {
				const url = new URL(item.url);
				navigate(`${url.pathname}${url.search}${url.hash}`);
			}}
		/>
	);
}
